import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Logo from "../../../assets/images/logo-white.png";
import Button from "../../input/Button";
import { fetchCanvases, fetchGroups } from "../../../helpers/api";
import { useQuery } from "react-query";
import { clsxm } from "../../../helpers/styling";
import { useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { slide as Menu } from 'react-burger-menu'

export default function DesktopNavbar({ userData }) {
    const { t } = useTranslation();

    const [menuOpen, setMenuOpen] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const { isLoading, error, data: canvasesData } = useQuery(['canvasesFeatured', 1], () => fetchCanvases(1, true));
    let canvases = canvasesData?.data.data || [];

    const { isLoading: areGroupsLoading, error: groupsError, data: groupsData } = useQuery(['groupsFeatured', 1], () => fetchGroups(1, true));
    let groups = groupsData?.data.data || [];

    const currentCanvasId = searchParams.get('fc');

    const navbarEntryClassName = 'relative flex shrink-0 whitespace-nowrap px-5 items-center py-6 text-sm leading-5 hover:bg-surface/25 focus:outline-none box-border rounded-t-xl z-10';
    const currentEntryClassName = 'bg-surface hover:bg-surface text-primary-text z-0';

    const renderGroup = (group) => {
        return {
            label: group.attributes.name,
            link: `/products?fc=g_${group.id}`,
            isCurrent: () => location.pathname === '/products' && currentCanvasId === `g_${group.id}`,
            className: group.attributes.textClass
        }
    }

    let savedOrderData = localStorage['orderData'] && JSON.parse(localStorage['orderData']);

    const navItems = [
        {
            label: t('Landing page'),
            link: "/",
            isCurrent: () => location.pathname === '/',
            mobileOnly: true
        },
        ...(groups.filter(group => !!group.attributes.priority).map((group) => renderGroup(group))),
        {
            label: t('All products'),
            link: "/products",
            isCurrent: () => location.pathname === '/products' && !currentCanvasId
        },
        ...(canvases.map((canvas) => {
            return {
                label: canvas.attributes.name,
                link: `/products?fc=${canvas.id}`,
                isCurrent: () => location.pathname === '/products' && currentCanvasId == canvas.id
            }
        })),
        ...(groups.filter(group => !group.attributes.priority).map((group) => renderGroup(group))),
        ...(savedOrderData && !location.pathname.startsWith('/order/') ? [
            {
                label: <><i className="fa fa-shopping-cart mr-2"></i> {t('Continue order')}</>,
                link: `/order/${savedOrderData.templateId}`,
                isCurrent: () => false,
                mobileOnly: true
            },
        ] : []),
        {
            label: <><i className="far fa-user mr-2"></i> {userData ? (userData.first_name ? `${userData.first_name}` : userData.email) : t('Sign up')}</>,
            link: userData ? "/profile" : "/signup",
            isCurrent: () => location.pathname.startsWith(userData ? "/profile" : "/signup"),
            mobileOnly: true
        },
        {
            label: <><i className="fa fa-sign-in mr-2"></i> {userData ? t('Sign out') : t('Sign in')}</>,
            link: userData ? "/signout" : "/signin",
            isCurrent: () => location.pathname === (userData ? "/signout" : "/signin"),
            mobileOnly: true
        },
    ];

    return <>
        <nav className={clsxm("bg-navbar-gray shadow-xl md:shadow-none px-4")}>
            <div className="flex gap-x-4 items-stretch overflow-hidden">
                <div className="shrink-0 flex items-center gap-x-4 justify-between w-full md:w-auto">
                    <Link to="/">
                        {/* Desktop Logo */}
                        <img className="h-6 md:h-8 min-w-full my-4 md:my-4" src={Logo} />
                    </Link>
                    <div className="block lg:hidden">
                        <Menu
                            disableAutoFocus 
                            isOpen={menuOpen} 
                            onStateChange={(state) => setMenuOpen(state.isOpen)}
                            styles={{
                                bmBurgerButton: {
                                    width: '24px',
                                    height: '20px',
                                }
                            }}
                        >
                            {navItems.map((navItem) => <Link to={navItem.link} key={navItem.link}>
                                <div
                                    className={
                                        'text-primary-text font-bold py-3 cursor-pointer px-16 border-r-4 ' +
                                        (navItem.isCurrent()
                                            ? 'bg-surface-selected border-accent'
                                            : 'hover:bg-surface-hovered border-surface-pressed')
                                    }
                                    onClick={(e) => {
                                        //   e.preventDefault();
                                        //   setPageId(id);
                                        //   setMenuVisible(false);
                                        setMenuOpen(false);
                                    }}
                                >
                                    <div className="flex items-center text-xl">
                                        <div className={navItem.className}>{navItem.label}</div>
                                    </div>
                                </div>
                            </Link>)}
                        </Menu>
                    </div>
                </div>
                <div className="flex lg:hidden grow" />
                <div className="lg:flex shrink grow min-w-0 flex-nowrap hidden text-white">
                    {navItems.filter((navItem) => !navItem.mobileOnly).map((navItem) => <Link to={navItem.link} key={navItem.link} >
                        <div className={clsxm(navbarEntryClassName, navItem.isCurrent() ? currentEntryClassName : 'hover:bg-white/25')}>
                            <div className={navItem.className}>{navItem.label}</div>
                            <div className={clsxm("absolute bottom-0 right-0 w-8 h-4 -mr-8 bg-surface", !navItem.isCurrent() && 'hidden')} />
                            <div className={clsxm("absolute bottom-0 right-0 w-8 h-4 -mr-8 bg-navbar-gray rounded-bl-xl", !navItem.isCurrent() && 'hidden')} />
                            <div className={clsxm("absolute bottom-0 left-0 w-8 h-4 -ml-8 bg-surface", !navItem.isCurrent() && 'hidden')} />
                            <div className={clsxm("absolute bottom-0 left-0 w-8 h-4 -ml-8 bg-navbar-gray rounded-br-xl", !navItem.isCurrent() && 'hidden')} />
                        </div>
                    </Link>)}
                </div>
                <div className="shrink-0 items-center bg-navbar-gray z-20 md:flex hidden">
                    <div className="flex items-center relative">
                        {savedOrderData && !location.pathname.startsWith('/order/') ? <Link to={`/order/${savedOrderData.templateId}`}>
                            <Button border={false} className="flex items-center">
                                <i className="fa fa-shopping-cart 2xl:mr-2" aria-hidden="true"></i>
                                <div className="hidden 2xl:block">
                                    {t('Continue order')}
                                </div>
                            </Button>
                        </Link> : ''}
                        {userData ? (
                            <div className="h-10 leading-none flex flex-row">
                                <Link to="/signout">
                                    <button className="h-full text-xs md:text-base text-white font-semibold py-2 px-4 uppercase">
                                        {t('Sign out')}
                                    </button>
                                </Link>
                                <Link to="/profile">
                                    <button className="px-3 h-full rounded-full border-2 border-surface text-white font-semibold text-xs md:text-sm bg-white bg-opacity-10 hover:bg-opacity-20 flex gap-x-2 justify-center items-center">
                                        <i className="far fa-user fa-lg"></i>
                                        <div className="hidden 2xl:block">
                                            {userData.first_name
                                                ? `${userData.first_name}`
                                                : userData.email}
                                        </div>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            <div className="h-10 leading-none flex flex-row">
                                <Link to="/signin">
                                    <button className="h-full text-white font-semibold py-2 px-4 uppercase text-xs md:text-base">
                                        {t('Sign in')}
                                    </button>
                                </Link>
                                <Link to="/signup">
                                    <Button border={false} className="h-full">
                                        {t('Sign up')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    </>;
}